import React, { memo, useEffect, useMemo, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Grid, ToggleButton, ToggleButtonGroup } from "@mui/material"
import { faSpinnerThird } from "@fortawesome/pro-light-svg-icons/faSpinnerThird"

import { LocalizedEnum } from "../../../../modules/localization/components/AppLocalized"

const LOADING_TIMEOUT = 1000

type StyleProps = Readonly<{
    isLight: boolean
    isMarginBottom: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { isLight, isMarginBottom }) => ({
    container: {
        display: "inline-flex",
        padding: ".25rem",
        borderRadius: ".875rem",
        backgroundColor: isLight ? theme.palette.tailwind.slate[200] : theme.palette.tailwind.slate[300],
        marginBottom: isMarginBottom ? "1.5rem" : "0",
    },
    wrap: {
        position: "relative",
        display: "flex",
        alignItems: "center",
    },
    toggleGroup: {
        gap: ".25rem",
        "& .MuiToggleButtonGroup-grouped:not(:last-of-type), & .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
            borderRadius: ".625rem",
        },
    },
    toggleButton: {
        position: "relative",
        zIndex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: ".5rem",
        textTransform: "none",
        border: "none",
        borderRadius: ".625rem",
        padding: ".5rem 1rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[600],
        transition: "color 0.2s ease",
        background: "transparent",

        "&:hover": {
            background: theme.palette.tailwind.slate[300],
        },
        "&.Mui-selected, &.Mui-selected:hover": {
            fontWeight: 600,
            background: "transparent",
        },
    },
    highlight: {
        position: "absolute",
        top: 0,
        left: 0,
        height: "100%",
        borderRadius: ".625rem",
        backgroundColor: theme.palette.common.white,
        boxShadow: theme.shadows[2],
        transition: "left 0.25s ease, width 0.25s ease",
        zIndex: 0,
    },
    buttonGreen: {
        "&.Mui-selected": {
            color: theme.palette.tailwind.green[700],
        },
    },
    buttonTeal: {
        "&.Mui-selected": {
            color: theme.palette.tailwind.teal[700],
        },
    },
    buttonBlue: {
        "&.Mui-selected": {
            color: theme.palette.tailwind.blue[700],
        },
    },
    buttonPurple: {
        "&.Mui-selected": {
            color: theme.palette.tailwind.purple[700],
        },
    },
    buttonAmber: {
        "&.Mui-selected": {
            color: theme.palette.tailwind.amber[700],
        },
    },
}))

export type ButtonConfigType<T extends string> = Readonly<{
    value: T
    icon: IconProp | null
    className: keyof ReturnType<typeof useStyles>["classes"]
    disabled?: boolean
}>

type StandardOrderTypeFilterProps<T extends string> = Readonly<{
    orderType: T
    setOrderType: (value: T) => void
    config: ButtonConfigType<T>[]
    isLight?: boolean
    isMarginBottom?: boolean
}>

type HighlightStyle = Readonly<{
    left: number
    width: number
}>

function StandardOrderTypeFilterInner<T extends string>({
    orderType,
    setOrderType,
    config,
    isLight = true,
    isMarginBottom = true,
}: StandardOrderTypeFilterProps<T>) {
    const { classes, cx } = useStyles({ isLight, isMarginBottom })
    const containerRef = useRef<HTMLDivElement>(null)
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const [highlightStyle, setHighlightStyle] = useState<HighlightStyle>({ left: 0, width: 0 })

    const [loadingButton, setLoadingButton] = useState<T | null>(null)

    useEffect(() => {
        if (!loadingButton) return

        if (loadingButton === orderType) {
            setLoadingButton(null)
            return
        }

        if (timerRef.current) {
            clearTimeout(timerRef.current)
        }

        timerRef.current = setTimeout(() => {
            setLoadingButton(null)
        }, LOADING_TIMEOUT)

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
            }
        }
    }, [orderType, loadingButton])

    useEffect(() => {
        const updateHighlight = () => {
            if (!containerRef.current) return

            const buttons = containerRef.current.querySelectorAll(".MuiToggleButton-root")
            const activeButton = Array.from(buttons).find((button) => button.getAttribute("value") === orderType)

            if (activeButton) {
                const { offsetLeft, offsetWidth } = activeButton as HTMLElement
                setHighlightStyle({ left: offsetLeft, width: offsetWidth })
            }
        }

        const observer = new ResizeObserver(updateHighlight)
        if (containerRef.current) {
            observer.observe(containerRef.current)
        }

        updateHighlight()

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current)
            }
        }
    }, [orderType, config])

    const handleChange = (_event: React.MouseEvent<HTMLElement>, newValue: T) => {
        if (newValue !== null && newValue !== orderType) {
            setLoadingButton(newValue)
            setOrderType(newValue)
        }
    }

    const filteredConfig = useMemo(() => config.filter(({ disabled }) => !disabled), [config])

    return (
        <Grid container justifyContent="flex-start" alignItems="center">
            <Box className={classes.container}>
                <Box ref={containerRef} className={classes.wrap}>
                    <Box
                        className={classes.highlight}
                        style={{ left: highlightStyle.left, width: highlightStyle.width }}
                    />
                    <ToggleButtonGroup
                        value={orderType}
                        exclusive
                        onChange={handleChange}
                        className={classes.toggleGroup}
                    >
                        {filteredConfig.map(({ value, icon, className }) => (
                            <ToggleButton
                                key={String(value)}
                                value={value}
                                className={cx(classes.toggleButton, className && classes[className])}
                                disableRipple
                            >
                                {loadingButton === value ? (
                                    <FontAwesomeIcon icon={faSpinnerThird} spin />
                                ) : (
                                    icon && <FontAwesomeIcon icon={icon} />
                                )}

                                <LocalizedEnum base="standard-order-type-filter-mode" enumValue={value}>
                                    <> {value}</>
                                </LocalizedEnum>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            </Box>
        </Grid>
    )
}

export const StandardOrderTypeFilter = memo(StandardOrderTypeFilterInner) as typeof StandardOrderTypeFilterInner
